import { useEffect, useRef, forwardRef, useImperativeHandle, useState } from 'react';
import { useProductSearch } from '../../hooks/productSearch/useProductSearch';
import "react-toastify/dist/ReactToastify.css";
import './placify.scss';
import { Product } from './types';

interface PlaciFyHandles {
    disconnect: () => void;
}
// Avatar for the AI Assistant
const AI_AVATAR_URL = "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=200&auto=format&fit=crop";
// const USER_AVATAR_URL = "https://images.unsplash.com/photo-1599566150163-29194dcaad36?q=80&w=200&auto=format&fit=crop";

const PlaciFy = forwardRef<PlaciFyHandles>((props, ref) => {
    const {
        searchProducts,
        products,
        message,
        loading,
        error,
        selectedProduct,
        setSelectedProduct,
        setMessage,
        setProducts,
        setLoading,
        suggestedQuestions: hookSuggestedQuestions,
        resetConversation
    } = useProductSearch();

    const [transcripts, setTranscripts] = useState<{ role: 'user' | 'assistant'; text: string; productSuggestions?: Product[]; }[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [typingText, setTypingText] = useState<string>('');
    const [showProducts, setShowProducts] = useState(false);
    const [pendingProducts, setPendingProducts] = useState<Product[]>([]);
    const [isListening, setIsListening] = useState(false);
    const [recognizedText, setRecognizedText] = useState('');
    const [textChatInput, setTextChatInput] = useState('');
    const [textChatLoading, setTextChatLoading] = useState(false);
    const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
    const [showSuggestedQuestions, setShowSuggestedQuestions] = useState(false);
    const [audioStates, setAudioStates] = useState<{ [key: number]: { isPlaying: boolean; isMuted: boolean; text: string } }>({});
    const [audioProvider, setAudioProvider] = useState<'gemini' | 'elevenlabs'>(() => {
        // Load from localStorage, default to 'gemini'
        const saved = localStorage.getItem('audioProvider');
        return (saved === 'elevenlabs' || saved === 'gemini') ? saved : 'gemini';
    });
    const typingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const audioRefsMap = useRef<{ [key: number]: HTMLAudioElement }>({});
    const recognitionRef = useRef<any>(null);
    const finalTranscriptRef = useRef<string>('');
    const speechSynthesisRef = useRef<HTMLAudioElement | SpeechSynthesisUtterance | null>(null);
    const hasSpokenRef = useRef<boolean>(false);
    const autoStartTriggeredRef = useRef<boolean>(false);
    const lastInputWasMicRef = useRef<boolean>(false);
    const selectedProductRef = useRef<Product | null>(null);

    const first_name = localStorage.getItem("firstName");
    const firstLetter = first_name?.charAt(0)?.toUpperCase();
    const scrollRef = useRef<HTMLDivElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const userHasScrolledUpRef = useRef<boolean>(false);

    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    // Check if user is near bottom (within 100px threshold)
    const isNearBottom = (element: HTMLDivElement): boolean => {
        const threshold = 100;
        return element.scrollHeight - element.scrollTop - element.clientHeight < threshold;
    };

    // Handle scroll events to detect if user manually scrolled up
    useEffect(() => {
        const scrollElement = scrollRef.current;
        if (!scrollElement) return;

        const handleScroll = () => {
            if (isNearBottom(scrollElement)) {
                userHasScrolledUpRef.current = false;
            } else {
                userHasScrolledUpRef.current = true;
            }
        };

        scrollElement.addEventListener('scroll', handleScroll);
        return () => {
            scrollElement.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Auto-scroll chat only if user is near bottom
    useEffect(() => {
        if (scrollRef.current && !userHasScrolledUpRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [transcripts, loading]);

    useEffect(() => {
        const scrollToBottom = () => {
            if (messagesEndRef.current && !userHasScrolledUpRef.current) {
                messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        };
        scrollToBottom();
    }, [transcripts]);

    // Typing effect for assistant messages
    useEffect(() => {
        if (message && !loading) {
            // Clear any existing typing interval
            if (typingIntervalRef.current) {
                clearInterval(typingIntervalRef.current);
            }

            setTypingText('');
            setShowProducts(false);
            setShowSuggestedQuestions(false); // Reset suggested questions visibility for new message
            // Set speech flag to true when /get_embedding response arrives - ensure speech plays
            hasSpokenRef.current = true; // Set to true immediately when message arrives from API
            // autoStartTriggeredRef is no longer used - microphone auto-start is disabled
            const fullText = message.trim();
            let currentIndex = 0;

            // Store products if they exist - capture current products value
            const productsToShow = products.length > 0 ? [...products] : [];
            setPendingProducts(productsToShow);

            // Add assistant message placeholder first and get message index
            let messageIndex = -1;
            setTranscripts(prev => {
                const lastMessage = prev[prev.length - 1];
                if (lastMessage && lastMessage.role === 'user') {
                    messageIndex = prev.length; // Index of new assistant message
                    return [
                        ...prev,
                        {
                            role: 'assistant',
                            text: '',
                            productSuggestions: undefined // Don't show products yet
                        }
                    ];
                } else if (lastMessage && lastMessage.role === 'assistant') {
                    messageIndex = prev.length - 1; // Index of existing assistant message being updated
                    return [
                        ...prev.slice(0, -1),
                        {
                            ...lastMessage,
                            text: '',
                            productSuggestions: undefined // Don't show products yet
                        }
                    ];
                }
                messageIndex = prev.length; // Fallback
                return prev;
            });

            // Start text-to-speech and typing animation
            // For ElevenLabs: wait for audio to be ready before starting typing
            // For Gemini: start typing immediately
            if (fullText && fullText.trim().length > 0) {
                // hasSpokenRef.current is already true (set above) - ensure speech plays
                // Reset all other messages' states to stopped, then set THIS message to playing
                setAudioStates(prev => {
                    const updated: typeof prev = {};
                    // Reset all existing messages to stopped
                    Object.keys(prev).forEach(key => {
                        const keyNum = parseInt(key);
                        updated[keyNum] = { ...prev[keyNum], isPlaying: false, isMuted: false };
                    });
                    // Set THIS message to playing (speaker icon)
                    updated[messageIndex] = { isPlaying: true, isMuted: false, text: fullText };
                    return updated;
                });

                if (audioProvider === 'elevenlabs') {
                    // For ElevenLabs: wait for audio to be ready, then start typing
                    speakText(fullText, messageIndex).then(() => {
                        // Audio has started playing, now start typing animation
                        startTypingAnimation();
                    }).catch((error) => {
                        console.error('Speech error:', error);
                        // If audio fails, still show text
                        hasSpokenRef.current = false;
                        startTypingAnimation();
                        // Reset state to show mute icon for THIS message only
                        setAudioStates(prev => ({
                            ...prev,
                            [messageIndex]: { ...(prev[messageIndex] || { isPlaying: false, isMuted: false, text: fullText }), isPlaying: false, isMuted: false }
                        }));
                    });
                } else {
                    // For Gemini: start typing immediately, don't wait for speech
                    startTypingAnimation();

                    // Start speech in parallel (don't block typing)
                    speakText(fullText, messageIndex).catch((error) => {
                        console.error('Speech error:', error);
                        // If audio fails, reset state to show mute icon for THIS message only
                        setAudioStates(prev => ({
                            ...prev,
                            [messageIndex]: { ...(prev[messageIndex] || { isPlaying: false, isMuted: false, text: fullText }), isPlaying: false, isMuted: false }
                        }));
                        hasSpokenRef.current = false; // Reset on error so it can retry
                    });
                }
            } else {
                // If no speech needed, start typing immediately
                startTypingAnimation();
            }

            function startTypingAnimation() {
                // Ensure typing starts immediately - set first character right away
                if (fullText.length > 0) {
                    setTypingText(fullText.substring(0, 1));
                    currentIndex = 1;
                }

                typingIntervalRef.current = setInterval(() => {
                    if (currentIndex < fullText.length) {
                        setTypingText(fullText.substring(0, currentIndex + 1));
                        currentIndex++;
                    } else {
                        if (typingIntervalRef.current) {
                            clearInterval(typingIntervalRef.current);
                            typingIntervalRef.current = null;
                        }
                        // Show products if any after typing completes
                        if (productsToShow.length > 0) {
                            setTimeout(() => {
                                setShowProducts(true);
                                // Show suggested questions 3 seconds after products are shown
                                setTimeout(() => {
                                    setShowSuggestedQuestions(true);
                                }, 3000); // 3 seconds delay after products shown
                            }, 500); // Small delay before showing products
                        } else {
                            // No products - show suggested questions 3 seconds after typing completes
                            setTimeout(() => {
                                setShowSuggestedQuestions(true);
                            }, 3000); // 3 seconds delay after typing completes
                        }
                        // Auto-start microphone disabled - user must manually click mic button
                    }
                }, 30); // 30ms per character for smooth typing effect
            }

            // Cleanup on unmount
            return () => {
                if (typingIntervalRef.current) {
                    clearInterval(typingIntervalRef.current);
                }
            };
        }
    }, [message, loading, products]);

    // Sync suggested questions from hook (for voice input)
    useEffect(() => {
        if (hookSuggestedQuestions && hookSuggestedQuestions.length > 0) {
            setSuggestedQuestions(hookSuggestedQuestions);
        } else if (hookSuggestedQuestions && hookSuggestedQuestions.length === 0) {
            // Clear suggested questions if hook returns empty array
            setSuggestedQuestions([]);
        }
    }, [hookSuggestedQuestions]);

    // Update transcripts with typing text
    useEffect(() => {
        // Always update transcripts when typingText changes (even if empty, to ensure message bubble shows)
        setTranscripts(prev => {
            const lastMessage = prev[prev.length - 1];
            if (lastMessage && lastMessage.role === 'assistant') {
                return [
                    ...prev.slice(0, -1),
                    {
                        ...lastMessage,
                        text: typingText || '', // Ensure text is always set, even if empty during initial typing
                        productSuggestions: showProducts ? pendingProducts : undefined
                    }
                ];
            }
            return prev;
        });
    }, [typingText, showProducts, pendingProducts]);

    // Update transcripts when products should be shown
    useEffect(() => {
        if (showProducts && pendingProducts.length > 0) {
            setTranscripts(prev => {
                const lastMessage = prev[prev.length - 1];
                if (lastMessage && lastMessage.role === 'assistant') {
                    return [
                        ...prev.slice(0, -1),
                        {
                            ...lastMessage,
                            text: lastMessage.text,
                            productSuggestions: pendingProducts
                        }
                    ];
                }
                return prev;
            });
        }
    }, [showProducts, pendingProducts]);

    // Helper function to remove emojis from text for speech synthesis
    // This keeps emojis visible on screen but removes them from speech
    const removeEmojis = (text: string): string => {
        // Remove emojis using regex pattern that matches emoji ranges
        return text.replace(
            /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{200D}]|[\u{FE00}-\u{FE0F}]|[\u{203C}-\u{3299}]/gu,
            ''
        ).trim();
    };

    // Unified text-to-speech function supporting both Gemini (SpeechSynthesis) and ElevenLabs
    const speakText = async (text: string, messageIndex?: number) => {
        if (!text || typeof window === 'undefined') {
            console.error('speakText early return - text:', text, 'window:', typeof window);
            return;
        }

        // Cancel any ongoing speech
        if (speechSynthesisRef.current) {
            if (speechSynthesisRef.current instanceof HTMLAudioElement) {
                speechSynthesisRef.current.pause();
                speechSynthesisRef.current.currentTime = 0;
            } else if (speechSynthesisRef.current instanceof SpeechSynthesisUtterance) {
                if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
                    window.speechSynthesis.cancel();
                }
            }
            speechSynthesisRef.current = null;
        }

        // Also ensure browser speech synthesis is cancelled
        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
            window.speechSynthesis.cancel();
        }

        // Stop any audio from audioRefsMap
        Object.values(audioRefsMap.current).forEach(audio => {
            if (audio && !audio.paused) {
                audio.pause();
                audio.currentTime = 0;
            }
        });

        // Route to appropriate provider
        if (audioProvider === 'elevenlabs') {
            return speakTextElevenLabs(text, messageIndex);
        } else {
            return speakTextGemini(text, messageIndex);
        }
    };

    // Text-to-speech using ElevenLabs API
    const speakTextElevenLabs = async (text: string, messageIndex?: number) => {
        try {
            // Get values from environment variables or use defaults
            const ELEVEN_API_KEY = process.env.REACT_APP_ELEVEN_API_KEY;
            const ELEVEN_MODEL = process.env.REACT_APP_ELEVEN_MODEL || "eleven_multilingual_v2";
            const VOICE_ID = process.env.REACT_APP_ELEVEN_VOICE_ID;

            // Check if API credentials are set
            if (!ELEVEN_API_KEY || !ELEVEN_MODEL || !VOICE_ID) {
                console.error('ElevenLabs API credentials missing!');
                throw new Error('ElevenLabs API credentials not configured.');
            }

            // Remove emojis from text before speaking
            const cleanedText = removeEmojis(text);

            if (!cleanedText) {
                console.warn('Text contains only emojis, skipping speech');
                return;
            }

            // Call ElevenLabs API
            const response = await fetch(
                `https://api.elevenlabs.io/v1/text-to-speech/${VOICE_ID}`,
                {
                    method: 'POST',
                    headers: {
                        'Accept': 'audio/mpeg',
                        'Content-Type': 'application/json',
                        'xi-api-key': ELEVEN_API_KEY,
                    },
                    body: JSON.stringify({
                        text: cleanedText,
                        model_id: ELEVEN_MODEL,
                        language_code: 'en',
                        voice_settings: {
                            stability: 0.5,
                            similarity_boost: 0.75,
                            style: 0.0,
                            use_speaker_boost: true
                        }
                    }),
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                console.error('ElevenLabs API error response:', errorText);
                throw new Error(`ElevenLabs API error: ${response.status} - ${errorText}`);
            }

            // Get audio data as blob
            const audioBlob = await response.blob();
            const audioUrl = URL.createObjectURL(audioBlob);

            // Create audio element and play
            const audio = new Audio(audioUrl);
            audio.playbackRate = 1.0;

            // Store audio reference by message index if provided
            if (messageIndex !== undefined) {
                audioRefsMap.current[messageIndex] = audio;
                setAudioStates(prev => ({
                    ...prev,
                    [messageIndex]: { isPlaying: true, isMuted: false, text: text }
                }));
            }

            audio.onplay = () => {
                if (messageIndex !== undefined) {
                    setAudioStates(prev => ({
                        ...prev,
                        [messageIndex]: { ...prev[messageIndex] || { isPlaying: false, isMuted: false, text: text }, isPlaying: true }
                    }));
                }
            };

            audio.onpause = () => {
                if (messageIndex !== undefined) {
                    setAudioStates(prev => ({
                        ...prev,
                        [messageIndex]: { ...prev[messageIndex] || { isPlaying: false, isMuted: false, text: text }, isPlaying: false }
                    }));
                }
            };

            audio.onended = () => {
                URL.revokeObjectURL(audioUrl);
                speechSynthesisRef.current = null;

                if (messageIndex !== undefined) {
                    setAudioStates(prev => ({
                        ...prev,
                        [messageIndex]: { ...prev[messageIndex] || { isPlaying: false, isMuted: false, text: text }, isPlaying: false, isMuted: false }
                    }));
                    delete audioRefsMap.current[messageIndex];
                }

                // Auto-start microphone if last input was from microphone
                if (lastInputWasMicRef.current && !isListening && !loading && !textChatLoading) {
                    setTimeout(() => {
                        if (!isListening) {
                            try {
                                startListening();
                            } catch (error) {
                                console.error('Error auto-starting microphone:', error);
                            }
                        }
                    }, 300);
                }
            };

            audio.onerror = (error) => {
                console.error('Audio playback error:', error);
                URL.revokeObjectURL(audioUrl);
                speechSynthesisRef.current = null;

                if (messageIndex !== undefined) {
                    setAudioStates(prev => ({
                        ...prev,
                        [messageIndex]: { ...prev[messageIndex] || { isPlaying: false, isMuted: false, text: text }, isPlaying: false, isMuted: false }
                    }));
                    delete audioRefsMap.current[messageIndex];
                }
            };

            // Store reference to audio for potential cancellation
            speechSynthesisRef.current = audio;

            // Wait for audio to be ready to play
            const audioReady = new Promise<void>((resolve, reject) => {
                if (audio.readyState >= 2) {
                    resolve();
                } else {
                    const onCanPlay = () => {
                        audio.removeEventListener('canplay', onCanPlay);
                        audio.removeEventListener('error', onError);
                        resolve();
                    };
                    const onError = (error: any) => {
                        console.error('Audio error while waiting for canplay:', error);
                        audio.removeEventListener('canplay', onCanPlay);
                        audio.removeEventListener('error', onError);
                        reject(error);
                    };
                    audio.addEventListener('canplay', onCanPlay);
                    audio.addEventListener('error', onError);

                    // Timeout after 10 seconds
                    setTimeout(() => {
                        console.warn('Audio ready timeout after 10 seconds');
                        audio.removeEventListener('canplay', onCanPlay);
                        audio.removeEventListener('error', onError);
                        resolve();
                    }, 10000);
                }
            });

            await audioReady;

            // Start playing audio
            try {
                const playPromise = audio.play();
                if (playPromise !== undefined) {
                    await playPromise;
                }
            } catch (playError) {
                console.error('Error playing audio:', playError);
                throw playError;
            }
        } catch (error) {
            console.error('ElevenLabs TTS error:', error);
            speechSynthesisRef.current = null;
            throw error;
        }
    };

    // Text-to-speech using browser's Web Speech API (Gemini-style)
    const speakTextGemini = async (text: string, messageIndex?: number) => {
        try {
            // Use browser's Web Speech API (SpeechSynthesis)
            // Voice configuration similar to useLiveGemini: voiceName 'Kore' (female, natural voice)
            if (!('speechSynthesis' in window)) {
                throw new Error('Speech synthesis is not supported in this browser.');
            }

            const synth = window.speechSynthesis;

            // Remove emojis from text before speaking (keep original text with emojis for display)
            const cleanedText = removeEmojis(text);

            if (!cleanedText) {
                console.warn('Text contains only emojis, skipping speech');
                return;
            }

            // Get available voices and select a high-quality female voice similar to 'Kore'
            const voices = synth.getVoices();
            let selectedVoice = voices.find(voice =>
                voice.name.includes('Google') &&
                voice.lang.startsWith('en') &&
                (voice.name.includes('Female') || voice.name.includes('F') || voice.name.includes('Samantha') || voice.name.includes('Karen'))
            ) || voices.find(voice =>
                voice.lang.startsWith('en-US') &&
                voice.name.toLowerCase().includes('female')
            ) || voices.find(voice => voice.lang.startsWith('en-US'));

            // Create speech utterance with cleaned text (emojis removed for speech)
            const utterance = new SpeechSynthesisUtterance(cleanedText);

            // Configure voice settings similar to 'Kore' style
            if (selectedVoice) {
                utterance.voice = selectedVoice;
            }
            utterance.lang = 'en-US';
            utterance.rate = 1.0; // Normal speaking rate
            utterance.pitch = 1.0; // Normal pitch
            utterance.volume = 1.0; // Full volume

            // Create a promise to handle audio playback similar to Audio element
            const audioReady = new Promise<void>((resolve, reject) => {
                utterance.onstart = () => {
                    resolve();
                };
                utterance.onerror = (error) => {
                    reject(error);
                };
            });

            // Store reference for cancellation
            speechSynthesisRef.current = utterance;

            // Store audio state for message index tracking
            if (messageIndex !== undefined) {
                setAudioStates(prev => ({
                    ...prev,
                    [messageIndex]: { isPlaying: true, isMuted: false, text: text }
                }));
            }

            // Handle speech events
            utterance.onstart = () => {
                if (messageIndex !== undefined) {
                    setAudioStates(prev => ({
                        ...prev,
                        [messageIndex]: { ...prev[messageIndex] || { isPlaying: false, isMuted: false, text: text }, isPlaying: true }
                    }));
                }
            };

            utterance.onend = () => {
                speechSynthesisRef.current = null;

                if (messageIndex !== undefined) {
                    setAudioStates(prev => ({
                        ...prev,
                        [messageIndex]: { ...prev[messageIndex] || { isPlaying: false, isMuted: false, text: text }, isPlaying: false, isMuted: false }
                    }));
                }

                // Auto-start microphone if last input was from microphone
                if (lastInputWasMicRef.current && !isListening && !loading && !textChatLoading) {
                    setTimeout(() => {
                        if (!isListening) {
                            try {
                                startListening();
                            } catch (error) {
                                console.error('Error auto-starting microphone:', error);
                            }
                        }
                    }, 300);
                }
            };

            utterance.onerror = (error) => {
                console.error('Speech synthesis error:', error);
                speechSynthesisRef.current = null;

                if (messageIndex !== undefined) {
                    setAudioStates(prev => ({
                        ...prev,
                        [messageIndex]: { ...prev[messageIndex] || { isPlaying: false, isMuted: false, text: text }, isPlaying: false, isMuted: false }
                    }));
                }
            };

            // Start speaking
            synth.speak(utterance);

            // Wait for speech to start
            await audioReady;
        } catch (error) {
            console.error('Speech synthesis error:', error);
            speechSynthesisRef.current = null;
            throw error;
        }
    };

    // Load voices when available
    useEffect(() => {
        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
            const loadVoices = () => {
                // Voices are loaded asynchronously
                const voices = window.speechSynthesis.getVoices();
                // Voices loaded, ready to use
            };

            if (window.speechSynthesis.onvoiceschanged !== undefined) {
                window.speechSynthesis.onvoiceschanged = loadVoices;
            }
            loadVoices();
        }
    }, []);

    // Helper function to stop any ongoing speech
    const stopAllSpeech = () => {
        // Stop browser speech synthesis (Gemini)
        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
            window.speechSynthesis.cancel();
        }

        // Stop ElevenLabs audio (HTMLAudioElement)
        if (speechSynthesisRef.current instanceof HTMLAudioElement) {
            speechSynthesisRef.current.pause();
            speechSynthesisRef.current.currentTime = 0;
        }

        // Stop all audio from audioRefsMap (ElevenLabs)
        Object.values(audioRefsMap.current).forEach(audio => {
            if (audio && !audio.paused) {
                audio.pause();
                audio.currentTime = 0;
            }
        });

        speechSynthesisRef.current = null;

        // Reset all audio states
        setAudioStates(prev => {
            const updated: typeof prev = {};
            Object.keys(prev).forEach(key => {
                updated[parseInt(key)] = { ...prev[parseInt(key)], isPlaying: false, isMuted: false };
            });
            return updated;
        });
    };

    // Helper function to parse markdown images and render them
    const parseMessageWithImages = (text: string) => {
        // Regex to match markdown image syntax: ![alt text](url)
        const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
        const parts: Array<{ type: 'text' | 'image'; content: string; alt?: string; url?: string }> = [];
        let lastIndex = 0;
        let match;

        while ((match = imageRegex.exec(text)) !== null) {
            // Add text before the image
            if (match.index > lastIndex) {
                parts.push({
                    type: 'text',
                    content: text.substring(lastIndex, match.index)
                });
            }
            // Add the image
            parts.push({
                type: 'image',
                alt: match[1] || 'Image',
                url: match[2],
                content: match[0] // Full match for replacement
            });
            lastIndex = match.index + match[0].length;
        }

        // Add remaining text after the last image
        if (lastIndex < text.length) {
            parts.push({
                type: 'text',
                content: text.substring(lastIndex)
            });
        }

        // If no images found, return the original text as a single text part
        if (parts.length === 0) {
            parts.push({
                type: 'text',
                content: text
            });
        }

        return parts;
    };

    const handleSearch = async (questionText?: string) => {
        const textToSearch = questionText || inputValue;
        if (!textToSearch.trim()) return;

        // Mark that last input was from microphone (voice input)
        lastInputWasMicRef.current = true;

        // Stop any ongoing speech (handles both Gemini and ElevenLabs)
        stopAllSpeech();
        // Reset hasSpokenRef - will be set to true when /get_embedding response arrives
        hasSpokenRef.current = false;

        // Clear any existing typing interval
        if (typingIntervalRef.current) {
            clearInterval(typingIntervalRef.current);
            typingIntervalRef.current = null;
        }
        setTypingText('');
        setShowProducts(false);
        setPendingProducts([]);
        // Immediately clear suggested questions when sending new request
        setSuggestedQuestions([]);
        setShowSuggestedQuestions(false);

        // Add user message
        setTranscripts(prev => [...prev, { role: 'user', text: textToSearch }]);
        setInputValue('');
        setRecognizedText('');

        // Search products - pass selectedProduct if available for product page chat
        // Use selectedProductRef.current to ensure we have the latest value (important for audio-to-text)
        await searchProducts(textToSearch, selectedProductRef.current || selectedProduct);
    };

    const handleProductSelect = (product: Product) => {
        setSelectedProduct(product);
    };

    // Shared function to handle text chat API request and response
    // Uses searchProducts from hook to avoid duplicating API logic
    const handleTextChatRequest = async (question: string) => {
        // Use the hook's searchProducts function instead of duplicating API logic
        // This will update products, message, and suggestedQuestions in the hook
        // Pass selectedProduct if available for product page chat
        await searchProducts(question, selectedProduct);

        // Sync suggested questions from hook (will be synced via useEffect)
        // The hook's searchProducts already handles conversation_id internally

        // Note: Transcripts will be updated by the existing useEffect that watches
        // message and products from the hook (lines 108-217)
        // The typing effect and speech will also be triggered by that useEffect
    };

    // Handle text chat submission
    const handleTextChatSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        const question = (textChatInput || inputValue).trim();
        if (!question || textChatLoading || loading) return;

        // Mark that last input was from text, not microphone
        lastInputWasMicRef.current = false;

        // Stop any ongoing speech before sending new request
        stopAllSpeech();

        setTextChatLoading(true);
        // Immediately clear suggested questions when sending new request
        setSuggestedQuestions([]);
        setShowSuggestedQuestions(false);
        // Clear all input states so the input field is empty after submission
        setTextChatInput('');
        setInputValue('');
        setRecognizedText('');

        // Add user message to transcripts
        setTranscripts(prev => [...prev, { role: 'user', text: question }]);

        try {
            await handleTextChatRequest(question);
        } catch (error) {
            console.error('Error in text chat:', error);
            setTranscripts(prev => [...prev, {
                role: 'assistant',
                text: 'Sorry, I encountered an error. Please try again.',
                productSuggestions: undefined
            }]);
            setSuggestedQuestions([]);
        } finally {
            setTextChatLoading(false);
            // Ensure input is cleared after submission completes (success or error)
            setTextChatInput('');
            setInputValue('');
            setRecognizedText('');
        }
    };

    // Handle clicking on a suggested question
    const handleSuggestedQuestionClick = async (question: string) => {
        if (textChatLoading || loading) return;

        // Mark that last input was from text, not microphone
        lastInputWasMicRef.current = false;

        // Stop any ongoing speech before sending new request
        stopAllSpeech();

        // Clear previous suggested questions
        setShowSuggestedQuestions(false);
        setSuggestedQuestions([]);

        // Set loading state
        setTextChatLoading(true);

        // Clear all input states
        setTextChatInput('');
        setInputValue('');
        setRecognizedText('');

        // Add user message to transcripts immediately
        setTranscripts(prev => [...prev, { role: 'user', text: question }]);

        try {
            await handleTextChatRequest(question);
        } catch (error) {
            console.error('Error in suggested question click:', error);
            setTranscripts(prev => [...prev, {
                role: 'assistant',
                text: 'Sorry, I encountered an error. Please try again.',
                productSuggestions: undefined
            }]);
            setSuggestedQuestions([]);
        } finally {
            setTextChatLoading(false);
            // Ensure input is cleared after submission completes (success or error)
            setTextChatInput('');
            setInputValue('');
            setRecognizedText('');
        }
    };

    // Keep selectedProductRef in sync with selectedProduct to ensure latest value is used in speech recognition
    useEffect(() => {
        selectedProductRef.current = selectedProduct;
    }, [selectedProduct]);

    // Initialize speech recognition using browser's Web Speech API
    // Note: Gemini API doesn't have a standalone speech-to-text REST endpoint
    // Using browser SpeechRecognition API which works well without API keys
    useEffect(() => {
        if (typeof window !== 'undefined' && ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
            const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
            const recognition = new SpeechRecognition();

            recognition.continuous = false;
            recognition.interimResults = true;
            recognition.lang = 'en-US';

            recognition.onstart = () => {
                setIsListening(true);
            };

            recognition.onresult = (event: any) => {
                let interimTranscript = '';
                let finalTranscript = '';

                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        finalTranscript += transcript + ' ';
                    } else {
                        interimTranscript += transcript;
                    }
                }

                if (finalTranscript) {
                    const finalText = finalTranscript.trim();
                    finalTranscriptRef.current = finalText;
                    setRecognizedText(finalText);
                    setInputValue(finalText);
                } else {
                    setRecognizedText(interimTranscript);
                    setInputValue(interimTranscript);
                }
            };

            recognition.onerror = (event: any) => {
                console.error('Speech recognition error:', event.error);
                setIsListening(false);
                if (event.error === 'no-speech') {
                    // User didn't speak, just stop listening
                }
            };

            recognition.onend = () => {
                setIsListening(false);
                // Auto-submit if we have recognized text
                // Note: Uses current selectedProduct value - if PDP page is open, will use product page chat endpoint
                setTimeout(() => {
                    const finalText = finalTranscriptRef.current || recognizedText || inputValue;
                    if (finalText.trim()) {
                        // handleSearch already passes selectedProduct to searchProducts
                        // If product is selected (PDP open), it will use product page chat endpoint
                        handleSearch(finalText.trim());
                        finalTranscriptRef.current = '';
                    }
                }, 500);
            };

            recognitionRef.current = recognition;
        }

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, []);

    const startListening = () => {
        if (recognitionRef.current && !isListening) {
            setRecognizedText('');
            setInputValue('');
            try {
                recognitionRef.current.start();
            } catch (error) {
                console.error('Error starting speech recognition:', error);
            }
        }
    };

    const stopListening = () => {
        if (recognitionRef.current && isListening) {
            recognitionRef.current.stop();
        }
    };

    const handleNewChat = () => {
        // Clear typing interval
        if (typingIntervalRef.current) {
            clearInterval(typingIntervalRef.current);
            typingIntervalRef.current = null;
        }
        // Stop speech recognition
        stopListening();
        // Stop all speech (handles both Gemini and ElevenLabs)
        stopAllSpeech();
        // Reset conversation in hook (clears conversation_id and error)
        resetConversation();
        // Clear all local state
        setTranscripts([]);
        setSelectedProduct(null);
        setInputValue('');
        setTextChatInput('');
        setTypingText('');
        setShowProducts(false);
        setPendingProducts([]);
        setRecognizedText('');
        setIsListening(false);
        setSuggestedQuestions([]);
        setShowSuggestedQuestions(false);
    };

    const disconnect = () => {
        // Clear typing interval
        if (typingIntervalRef.current) {
            clearInterval(typingIntervalRef.current);
            typingIntervalRef.current = null;
        }
        // Stop speech recognition
        stopListening();
        // Stop all speech (handles both Gemini and ElevenLabs)
        stopAllSpeech();
        setTranscripts([]);
        setSelectedProduct(null);
        setInputValue('');
        setTypingText('');
        setShowProducts(false);
        setPendingProducts([]);
        setRecognizedText('');
        setIsListening(false);
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (typingIntervalRef.current) {
                clearInterval(typingIntervalRef.current);
            }
            // Stop speech recognition
            stopListening();
            // Stop all speech (handles both Gemini and ElevenLabs)
            stopAllSpeech();
        };
    }, []);

    useImperativeHandle(ref, () => ({
        disconnect
    }));

    const renderProduct = (product: Product, idx: number | string) => {
        return (
            <div
                key={idx}
                className="product-card"
                onClick={() => handleProductSelect(product)}
                style={{ cursor: 'pointer' }}
            >
                <div className="product-content">
                    <img
                        src={product.imageUrl}
                        alt={product.name}
                        className="product-image"
                    />
                    <ul className="product-details">
                        <li> {product.name}</li>
                        {/* <li><strong>Price:</strong> {formatter.format(product.price)}</li> */}
                    </ul>
                </div>
            </div>
        );
    };

    return (
        <div className="placify-wrapper">
            {/* Product Detail Panel - Left Side Outside Chatbox */}
            {selectedProduct && (
                <div className="product-detail-panel" style={{ display: 'block' }}>
                    <button
                        className="product-detail-close"
                        onClick={() => setSelectedProduct(null)}
                        title="Close"
                    >
                        ×
                    </button>
                    <div className="product-detail-content">
                        {/* Product Image */}
                        <div className="product-detail-image-container">
                            <img
                                src={selectedProduct.imageUrl}
                                alt={selectedProduct.name}
                                className="product-detail-image"
                            />
                        </div>
                        <h2 className="product-detail-name">{selectedProduct.name}</h2>
                        <p className="product-detail-price">{formatter.format(selectedProduct.price)}</p>
                        {selectedProduct.designer && (
                            <>
                                <hr className="product-detail-divider" />
                                <div className="product-detail-field">
                                    <strong>Designer</strong>
                                    <p>{selectedProduct.designer}</p>
                                </div>
                            </>
                        )}
                        {selectedProduct.brand_collection && (
                            <>
                                <hr className="product-detail-divider" />
                                <div className="product-detail-field">
                                    <strong>Brand Collection</strong>
                                    <p>{selectedProduct.brand_collection}</p>
                                </div>
                            </>
                        )}
                        {selectedProduct.description && (
                            <>
                                <hr className="product-detail-divider" />
                                <div className="product-detail-field">
                                    <strong>Description</strong>
                                    <p>{selectedProduct.description}</p>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            <div className="placify-container">
                {/* Main Layout */}
                {/* <main className="pt-24 pb-28 min-h-screen md:h-screen flex flex-col md:flex-row gap-6 p-4 md:p-6 max-w-[1800px] mx-auto mt-[70px] z-10 relative"> */}
                {/* Middle Column: AI Interaction Zone */}
                <div className="placify-main-panel">

                    {/* Chat Transcript Area */}
                    <div className="chat-transcript-area" ref={scrollRef}>
                        {transcripts.length === 0 && (
                            <div className="empty-state">
                                <div className="avatar-large">
                                    <img src={AI_AVATAR_URL} alt="RysunAI" />
                                </div>
                                <div className="placify-pre-text space-y-2 max-w-md">
                                    <h3>"Hi, I'm RysunAI!"</h3>
                                    <p>I can help you find products. Ask me about products (e.g., "hi, how are you?")</p>
                                </div>
                            </div>
                        )}

                        {transcripts.map((msg, idx) => (
                            <div key={idx} className={`chat-message ${msg.role}`}>

                                {/* Avatar */}
                                <div className="avatar-container">
                                    <div className={`avatar-wrapper ${msg.role}`}>
                                        {msg.role === 'assistant' ? <img
                                            src={AI_AVATAR_URL}
                                            alt={msg.role}
                                        /> : <div className="profile-img">{firstLetter}</div>}
                                    </div>
                                </div>

                                {/* Bubble */}
                                <div className="msg-bubble-container">
                                    {/* Show message bubble if text exists (including empty string during typing) */}
                                    {(msg.text !== undefined && msg.text !== null) && (
                                        <div className={`message-bubble ${msg.role}`}>
                                            {msg.role === 'assistant' && msg.text && (() => {
                                                // Check only THIS specific message's playing state
                                                // isPlaying = true means speech is playing (show speaker icon)
                                                // isPlaying = false means speech is stopped (show mute icon)
                                                const isPlaying = audioStates[idx]?.isPlaying || false;
                                                const isMuted = audioStates[idx]?.isMuted || false;

                                                // Try to find audio - first check audioRefsMap (ElevenLabs), then speechSynthesisRef (Gemini)
                                                let audio = audioRefsMap.current[idx];
                                                if (!audio && speechSynthesisRef.current instanceof HTMLAudioElement) {
                                                    audio = speechSynthesisRef.current;
                                                }

                                                return (
                                                    <button
                                                        className="speaker-button"
                                                        onClick={async (e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();

                                                            if (audioProvider === 'elevenlabs' && audio) {
                                                                // ElevenLabs: handle mute/unmute
                                                                const currentMuted = isMuted || audio.muted || false;

                                                                if (currentMuted) {
                                                                    // Unmute and restart from beginning
                                                                    audio.muted = false;
                                                                    audio.currentTime = 0;
                                                                    try {
                                                                        await audio.play();
                                                                        setAudioStates(prev => ({
                                                                            ...prev,
                                                                            [idx]: { ...(prev[idx] || { isPlaying: false, isMuted: false, text: msg.text }), isMuted: false, isPlaying: true }
                                                                        }));
                                                                    } catch (error) {
                                                                        console.error('Error playing audio:', error);
                                                                    }
                                                                } else {
                                                                    // Mute
                                                                    audio.muted = true;
                                                                    audio.pause();
                                                                    setAudioStates(prev => ({
                                                                        ...prev,
                                                                        [idx]: { ...(prev[idx] || { isPlaying: false, isMuted: false, text: msg.text }), isMuted: true, isPlaying: false }
                                                                    }));
                                                                }
                                                            } else {
                                                                // Gemini: handle play/stop
                                                                if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
                                                                    const synth = window.speechSynthesis;

                                                                    // If speech is playing (showing speaker icon), stop it (show mute icon)
                                                                    if (isPlaying && synth.speaking) {
                                                                        synth.cancel();
                                                                        speechSynthesisRef.current = null;
                                                                        setAudioStates(prev => ({
                                                                            ...prev,
                                                                            [idx]: { ...(prev[idx] || { isPlaying: false, isMuted: false, text: msg.text }), isPlaying: false, isMuted: false }
                                                                        }));
                                                                    } else if (msg.text) {
                                                                        // Speech is stopped (showing mute icon), start it (show speaker icon)
                                                                        if (synth.speaking) {
                                                                            synth.cancel();
                                                                            speechSynthesisRef.current = null;
                                                                            setAudioStates(prev => {
                                                                                const updated: typeof prev = {};
                                                                                Object.keys(prev).forEach(key => {
                                                                                    const keyNum = parseInt(key);
                                                                                    if (keyNum !== idx) {
                                                                                        updated[keyNum] = { ...prev[keyNum], isPlaying: false, isMuted: false };
                                                                                    }
                                                                                });
                                                                                return updated;
                                                                            });
                                                                        }
                                                                        const messageIndex = idx;
                                                                        setAudioStates(prev => ({
                                                                            ...prev,
                                                                            [messageIndex]: { isPlaying: true, isMuted: false, text: msg.text }
                                                                        }));
                                                                        speakText(msg.text, messageIndex);
                                                                    }
                                                                } else if (msg.text) {
                                                                    // Fallback: start speech if synthesis not available
                                                                    const messageIndex = idx;
                                                                    setAudioStates(prev => ({
                                                                        ...prev,
                                                                        [messageIndex]: { isPlaying: true, isMuted: false, text: msg.text }
                                                                    }));
                                                                    speakText(msg.text, messageIndex);
                                                                }
                                                            }
                                                        }}
                                                        title={isPlaying && !isMuted ? "Stop speech" : "Play speech"}
                                                    >
                                                        {isPlaying && !isMuted ? (
                                                            // Speaker icon when THIS message's speech is playing (click to stop)
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                                                                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                                                            </svg>
                                                        ) : (
                                                            // Mute icon (speaker with X) when THIS message's speech is stopped (click to play)
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
                                                                <line x1="23" y1="9" x2="17" y2="15"></line>
                                                                <line x1="17" y1="9" x2="23" y2="15"></line>
                                                            </svg>
                                                        )}
                                                    </button>
                                                );
                                            })()}
                                            {/* Display text with emojis preserved - emojis are only removed for speech */}
                                            {/* Parse and render images from markdown syntax */}
                                            <div className="message-content">
                                                {msg.text && parseMessageWithImages(msg.text).map((part, partIdx) => {
                                                    if (part.type === 'image' && part.url) {
                                                        return (
                                                            <div key={partIdx} className="message-image-container">
                                                                <img
                                                                    src={part.url}
                                                                    alt={part.alt || 'Product image'}
                                                                    className="message-image"
                                                                    onError={(e) => {
                                                                        // Hide image if it fails to load
                                                                        (e.target as HTMLImageElement).style.display = 'none';
                                                                    }}
                                                                />
                                                                {part.alt && part.alt !== 'Image' && (
                                                                    <div className="message-image-caption">{part.alt}</div>
                                                                )}
                                                            </div>
                                                        );
                                                    } else {
                                                        // Render text, but remove the markdown image syntax
                                                        const textWithoutImages = part.content.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '').trim();
                                                        return textWithoutImages ? (
                                                            <p key={partIdx}>{textWithoutImages}</p>
                                                        ) : null;
                                                    }
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    {msg.role === 'assistant' && (msg?.productSuggestions && msg?.productSuggestions?.length > 0) && (
                                        <div className="h-[300px] max-h-[320px] min-h-[320px] overflow-x-auto flex flex-row gap-2 flex-nowrap product-cards-scroll">
                                            {msg?.productSuggestions?.map((product, productIdx) => (
                                                renderProduct(product, productIdx)
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        <span ref={messagesEndRef}></span>


                        {/* Typing Indicator */}
                        {(loading || textChatLoading) && (
                            <div className="typing-indicator">
                                <div className="avatar-container">
                                    <div className="avatar-wrapper assistant">
                                        <img src={AI_AVATAR_URL} alt="RysunAI" />
                                    </div>
                                </div>
                                <div className="dots-container">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="error-message">
                                {error}
                            </div>
                        )}
                    </div>

                    {/* Suggested Questions - Above Input */}
                    {suggestedQuestions.length > 0 && showSuggestedQuestions && (
                        <div className="suggested-questions-container">
                            <div className="suggested-questions-list">
                                {suggestedQuestions.slice(0, 3).map((question, idx) => (
                                    <button
                                        key={idx}
                                        className="suggested-question-button"
                                        onClick={() => handleSuggestedQuestionClick(question)}
                                        disabled={loading || textChatLoading}
                                        aria-label={`Ask: ${question}`}
                                    >
                                        <span className="question-text">{question}</span>
                                        <svg className="arrow-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="5" y1="12" x2="19" y2="12"></line>
                                            <polyline points="12 5 19 12 12 19"></polyline>
                                        </svg>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Combined Input Interface - Text and Voice */}
                    <div className="voice-interface-area">
                        {/* Audio Provider Toggle */}
                        <div className="audio-provider-toggle-container">
                            <label className="audio-provider-toggle-label">
                                {/* <span className="toggle-label-text">Audio Provider:</span> */}
                                <div className="toggle-switch-wrapper">
                                    <button
                                        className={`toggle-option ${audioProvider === 'gemini' ? 'active' : ''}`}
                                        onClick={() => {
                                            setAudioProvider('gemini');
                                            localStorage.setItem('audioProvider', 'gemini');
                                            stopAllSpeech();
                                        }}
                                        title="Use Gemini (Browser Speech Synthesis)"
                                    >
                                        Sofia  {/* Gemini */}
                                    </button>
                                    <button
                                        className={`toggle-option ${audioProvider === 'elevenlabs' ? 'active' : ''}`}
                                        onClick={() => {
                                            setAudioProvider('elevenlabs');
                                            localStorage.setItem('audioProvider', 'elevenlabs');
                                            stopAllSpeech();
                                        }}
                                        title="Use ElevenLabs (Premium Voice)"
                                    >
                                        RysunAI {/*  Eleven LABS */}
                                    </button>
                                </div>
                            </label>
                        </div>
                        <div className="input-interface-container">
                            {/* New Chat Button - Left side */}
                            <button
                                className="new-chat-button"
                                onClick={handleNewChat}
                                disabled={loading || textChatLoading}
                                title="Start new chat"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                            </button>
                            {/* Text Input - Adjusted width to accommodate new chat button */}
                            <form
                                className="text-input-form"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    const question = (textChatInput || inputValue).trim();
                                    if (question && !textChatLoading && !loading) {
                                        handleTextChatSubmit(e);
                                    }
                                }}
                                style={{ flex: '1', width: 'auto', marginRight: '0.5rem' }}
                            >
                                <input
                                    type="text"
                                    className="combined-text-input"
                                    placeholder="Type your question or click mic to speak..."
                                    value={textChatInput || recognizedText || inputValue || ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setTextChatInput(value);
                                        setInputValue(value);
                                        // Clear recognizedText when user types manually
                                        if (value && recognizedText) {
                                            setRecognizedText('');
                                        }
                                    }}
                                    disabled={loading || textChatLoading}
                                />
                            </form>

                            {/* Send/Rocket Button or Microphone Button - 10% width */}
                            <div className="mic-button-container" style={{ flex: '0 0 10%', width: '10%' }}>
                                {(() => {
                                    const hasText = (textChatInput || inputValue || recognizedText || '').trim().length > 0;

                                    if (hasText) {
                                        // Show rocket icon when there's text
                                        return (
                                            <button
                                                className={`mic-button ${loading || textChatLoading ? 'disabled' : ''}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    const question = (textChatInput || inputValue || recognizedText || '').trim();
                                                    if (question && !textChatLoading && !loading) {
                                                        handleTextChatSubmit(e);
                                                    }
                                                }}
                                                disabled={loading || textChatLoading}
                                                title="Send message"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"></path>
                                                    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"></path>
                                                    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"></path>
                                                    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"></path>
                                                </svg>
                                            </button>
                                        );
                                    } else {
                                        // Show microphone icon when there's no text
                                        return (
                                            <button
                                                className={`mic-button ${isListening ? 'listening' : ''} ${loading || textChatLoading ? 'disabled' : ''}`}
                                                onClick={isListening ? stopListening : startListening}
                                                disabled={loading || textChatLoading}
                                                title={isListening ? 'Stop listening' : 'Click to speak'}
                                            >
                                                {isListening ? (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <rect x="6" y="6" width="12" height="12" rx="2"></rect>
                                                    </svg>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                                                        <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                                                        <line x1="12" y1="19" x2="12" y2="23"></line>
                                                        <line x1="8" y1="23" x2="16" y2="23"></line>
                                                    </svg>
                                                )}
                                            </button>
                                        );
                                    }
                                })()}
                            </div>
                        </div>

                    </div>
                </div>
                {/* </main> */}
            </div>
        </div>
    );
});

export default PlaciFy;
